<script setup lang="ts">
import type { IEmits, IProps } from './DataViewFilters.types'
import type { IDataViewFiltersLabels } from '../DataView.types'

const props = withDefaults(defineProps<IProps>(), {
  showSearch: false,
  showMonths: false,
  showCategory: true,
  showYear: true,
  hasActiveFilters: false,
  isLoading: false,
})

const category = defineModel<string | number | null | undefined>('category', { default: undefined })
const year = defineModel<number[]>('year', { default: () => [] })
const months = defineModel<number[]>('months', { default: () => [] })
const searchValue = defineModel<string>('searchValue', { default: '' })

const emit = defineEmits<IEmits>()

const categoryOptions = computed(() => props.filterOptions?.categoryOptions ?? [])
const yearOptions = computed(() => props.filterOptions?.yearOptions ?? [])
const monthOptions = computed(() => props.filterOptions?.monthOptions ?? [])
const searchOptions = computed(() => props.filterOptions?.searchOptions ?? [])
const searchEmptyText = computed(() => props.filterOptions?.searchEmptyText)

const labels = computed<IDataViewFiltersLabels>(() => ({
  categoryLabel: 'common.dataView.filters.category',
  searchLabel: 'common.dataView.filters.searchLabel',
  searchPlaceholder: 'common.dataView.filters.searchPlaceholder',
  searchSubmit: 'common.dataView.filters.searchSubmit',
  yearLabel: 'common.dataView.filters.yearLabel',
  yearPlaceholder: 'common.dataView.filters.yearPlaceholder',
  monthLabel: 'common.dataView.filters.monthLabel',
  monthPlaceholder: 'common.dataView.filters.monthPlaceholder',
  resultsLabel: 'common.dataView.filters.resultsLabel',
  resultsUnit: 'common.dataView.filters.resultsUnit',
  clearFilters: 'common.dataView.filters.clear',
  ...props.labels,
}))

const showResults = ref(props.hasActiveFilters)

const onSearch = (value = searchValue.value) => {
  emit('search', value.trim())
}

const onClear = () => {
  searchValue.value = ''
  emit('clear')
}

watch(() => props.hasActiveFilters, (active) => {
  if (!active) showResults.value = false
})

watch(() => props.isLoading, (loading) => {
  if (!loading) showResults.value = props.hasActiveFilters
})
</script>

<template>
  <div class="dataViewFilters">
    <div
      v-if="showCategory"
      class="dataViewFilters__category"
    >
      <p class="dataViewFilters__categoryLabel">
        {{ $t(labels.categoryLabel!) }}
      </p>

      <UiTabs
        v-model="category"
        :options="categoryOptions"
      />
    </div>

    <div class="dataViewFilters__row">
      <div
        v-if="showSearch"
        class="dataViewFilters__search"
      >
        <UiSearchbar
          v-model="searchValue"
          class="dataViewFilters__searchInput"
          :options="searchOptions"
          :empty-text="searchEmptyText"
          :label="$t(labels.searchLabel!)"
          :placeholder="$t(labels.searchPlaceholder!)"
          @submit="onSearch"
        />

        <UiButton
          class="dataViewFilters__searchButton"
          size="big"
          variant="primary"
          @clicked="onSearch"
        >
          {{ $t(labels.searchSubmit!) }}
        </UiButton>
      </div>

      <UiMultiselect
        v-if="showYear"
        v-model="year"
        :options="yearOptions"
        :label="$t(labels.yearLabel!)"
        :placeholder="$t(labels.yearPlaceholder!)"
      />

      <UiMultiselect
        v-if="showMonths"
        v-model="months"
        class="dataViewFilters__months"
        :options="monthOptions"
        :label="$t(labels.monthLabel!)"
        :placeholder="$t(labels.monthPlaceholder!)"
      />
    </div>

    <div class="dataViewFilters__footer">
      <Transition name="fade">
        <p
          v-if="showResults"
          class="dataViewFilters__results"
        >
          <span class="dataViewFilters__resultsLabel">{{ $t(labels.resultsLabel!) }}</span>

          <span
            class="dataViewFilters__resultsCount"
            :class="{ 'dataViewFilters__resultsCount--loading': isLoading }"
          >{{ total }} {{ $t(labels.resultsUnit!, total) }}</span>
        </p>
      </Transition>

      <UiButton
        class="dataViewFilters__clear"
        size="small"
        variant="tertiary"
        :disabled="!hasActiveFilters"
        @clicked="onClear"
      >
        {{ $t(labels.clearFilters!) }}
      </UiButton>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.dataViewFilters {
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  gap: 2.4rem;
  margin-bottom: 3.2rem;

  &__categoryLabel {
    @include h9;
    margin-bottom: 2.4rem;
    color: $text-400;
  }

  &__row {
    display: grid;
    grid-template-columns: 1fr;
    gap: 0.8rem;
    width: 100%;
  }

  &__search {
    display: flex;
    flex-direction: column;
    gap: 1.2rem;

    > .dataViewFilters__searchButton {
      width: 100%;
    }
  }

  &__searchInput {
    flex: 1;
  }

  &__searchButton.button {
    flex-shrink: 0;
    height: 5rem;
    padding: 1.4rem 2.4rem;
    font-size: 1.6rem;

    &::after {
      content: '';
      width: 1.6rem;
      height: 1.6rem;
      margin-left: 0.8rem;
      background-image: url('/icons/icn_arrow_right_white.svg');
      background-repeat: no-repeat;
      background-position: center;
      background-size: contain;
    }
  }

  &__months :deep(.uiMultiselect__tag),
  &__months :deep(.uiMultiselect__option) {
    text-transform: capitalize;
  }

  &__footer {
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 1.6rem;
    width: 100%;
  }

  &__results {
    @include body-2;
  }

  &__clear {
    margin-left: auto;
  }

  &__resultsLabel {
    color: $text-200;
  }

  &__resultsCount {
    margin-left: 0.4rem;
    color: $text-400;
    transition: opacity 0.25s ease;

    &--loading {
      opacity: 0;
    }
  }

  @include atXS {
    &__row {
      grid-template-columns: 1fr 1fr;
    }

    &__search {
      flex-direction: row;
      align-items: flex-end;
      grid-column: 1 / -1;

      > .dataViewFilters__searchButton {
        width: auto;
      }
    }
  }

  @include atS {
    &__row {
      align-items: flex-end;
      grid-template-columns: 2fr 1fr 1fr;
    }

    &__search {
      grid-column: auto;
    }
  }
}
</style>
